import React from "react";
import { GenericTable, GenericTableColumn } from "@ui-components/Table/GenericTable";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import GenericButton from "@ui-components/Button/GenericButton";
import { ReactComponent as IconChevronDown } from "@assets/Icons/IconChevron-Down.svg";
import { splitLogTimestamp } from "ctxReflector/components/L3overview/logTimestamp";
import { levelTagVariant, splitMsgId } from "ctxReflector/components/L3overview/logMessage";
import type { LogRow } from "./logGrouping";
import type { ReflectorLogRecord } from "./logRecord";
import { ceaseLabel, extractPeerState, peerIpOf, splitAtIp } from "./logContext";

/** Points right while the row is closed and down while it is open. */
function ExpandToggle({ isExpanded, onToggle }: { isExpanded: boolean; onToggle: () => void }) {
  return (
    <GenericButton
      onClick={onToggle}
      icon={
        <IconChevronDown
          className={`w-4 h-4 text-semantic-text-secondary transition-transform ${
            isExpanded ? "" : "-rotate-90"
          }`}
        />
      }
      isIconOnly
      variant="secondary"
      shape="ghost"
      size="sm"
      aria-expanded={isExpanded}
      aria-label={isExpanded ? "Collapse log" : "Expand log"}
    />
  );
}

function TimestampCell({ value }: { value: string }) {
  const parts = splitLogTimestamp(value);
  if (!parts) {
    return <span className="text-sm text-semantic-text-secondary">{value}</span>;
  }
  return (
    <div className="flex flex-col leading-tight whitespace-nowrap">
      <span className="text-sm text-semantic-text-primary">
        {parts.time}
        <span className="text-semantic-text-secondary">{parts.fraction}</span>
      </span>
      <span className="text-xs text-semantic-text-secondary">{parts.date}</span>
    </div>
  );
}

// HH:mm:ss of a record timestamp; anything else is shown as it came.
const clockTime = (timestamp: string) => splitLogTimestamp(timestamp)?.time ?? timestamp;

const PEER_LINK_CLASS =
  "inline p-0 bg-transparent underline decoration-dotted underline-offset-2 text-semantic-text-brand hover:decoration-solid focus-visible:outline focus-visible:outline-2 focus-visible:outline-semantic-border-brand-secondary";

/**
 * `text` with the peer's address as a link-styled button that hands the address
 * to `onFilterPeer`. Without a peer or a handler it is just the text.
 */
function MessageText({
  text,
  peerIp,
  onFilterPeer,
}: {
  text: string;
  peerIp: string | null;
  onFilterPeer?: (ip: string) => void;
}) {
  if (!peerIp || !onFilterPeer) return <>{text}</>;
  return (
    <>
      {splitAtIp(text, peerIp).map((segment, index) =>
        segment.isIp ? (
          <button
            key={index}
            type="button"
            className={PEER_LINK_CLASS}
            onClick={(event) => {
              // Not a click on the row.
              event.stopPropagation();
              onFilterPeer(segment.text);
            }}
            title={`Filter by ${segment.text}`}
          >
            {segment.text}
          </button>
        ) : (
          <React.Fragment key={index}>{segment.text}</React.Fragment>
        )
      )}
    </>
  );
}

/**
 * How many lines a group holds. Not a GenericTag: that one nests two padded
 * boxes, so it cannot go below the MSGID chip's size.
 */
function CountChip({ count }: { count: number }) {
  return (
    <span className="inline-flex items-center rounded-sm bg-semantic-bg-brand-secondary px-1.5 py-0.5 text-[11px] font-medium leading-none text-semantic-text-primary">
      {`×${count}`}
    </span>
  );
}

/** The Cease the peer closed with, small and grey: context, not an alert. */
function CeaseChip({ label }: { label: string }) {
  return (
    <span className="inline-flex flex-shrink-0 items-center rounded-sm border border-semantic-border-secondary bg-semantic-bg-secondary px-1.5 py-0.5 text-xs text-semantic-text-secondary">
      {label}
    </span>
  );
}

/**
 * The `×N` chip and the MSGID chip sit on their own line above the text.
 * GenericTag is taller than a line of text (its padding applies twice), so
 * inline in a wrapping message it pushed the line apart and the next line ran
 * underneath it. A group adds, under the text, how many lines it holds and the
 * span they cover. The Cease chip is the exception: it is not a GenericTag
 * (that one's border is a raw gray) and follows the text on its own line box.
 */
function MessageCell<TRaw>({
  row,
  wrap,
  onFilterPeer,
}: {
  row: LogRow<TRaw>;
  wrap: boolean;
  onFilterPeer?: (ip: string) => void;
}) {
  const { message } = row.record;
  const { msgId, rest } = splitMsgId(message);
  const isGroup = row.count > 1;
  const peerCease = extractPeerState(message)?.cease;
  return (
    <div className="flex flex-col items-start gap-1 min-w-0" title={message}>
      {(isGroup || msgId) && (
        <div className="flex items-center gap-1">
          {isGroup && <CountChip count={row.count} />}
          {msgId && (
            <GenericTag label={msgId} variant="neutral" size="small" rounded="small" />
          )}
        </div>
      )}
      <div
        className={`flex max-w-full min-w-0 items-baseline gap-2 ${wrap ? "flex-wrap" : ""}`}
      >
        <span
          className={`min-w-0 max-w-full text-sm text-semantic-text-primary ${
            wrap ? "break-words" : "truncate"
          }`}
        >
          <MessageText text={rest} peerIp={peerIpOf(message)} onFilterPeer={onFilterPeer} />
        </span>
        {peerCease && <CeaseChip label={ceaseLabel(peerCease)} />}
      </div>
      {isGroup && (
        <span className="text-xs text-semantic-text-secondary">
          {row.count} lines · {clockTime(row.first)} → {clockTime(row.last)}
        </span>
      )}
    </div>
  );
}

/** Background by severity; the tokens are the theme's, so dark mode follows. */
export function logRowClassName(level: string): string {
  switch (level.trim().toUpperCase()) {
    case "WARNING":
    case "WARN":
      return "bg-semantic-bg-warning-primary";
    case "ERROR":
      return "bg-semantic-bg-danger-primary";
    default:
      return "";
  }
}

interface ReflectorLogColumnsOptions<TRaw> {
  wrapMessages: boolean;
  /** Timestamp and Level sort; the page opts in. */
  sortable: boolean;
  /** Shown between Level and Message. */
  extraColumns: GenericTableColumn<LogRow<TRaw>>[];
  /** When set, the first column is the chevron that opens the row. */
  hasExpandedRow: boolean;
  /** Without it the peer's address in the message is plain text. */
  onFilterPeer?: (ip: string) => void;
}

/**
 * Every column but Message has a fixed width and truncates; Message takes the rest.
 * [chevron] | Timestamp | Level | …extra | Message — the CTX-1354
 * proposal on the platform's table conventions: plain headers, Level and the
 * MSGID chip as `GenericTag`. Message is shown whole, on one truncated line or
 * wrapped. The chevron lives in this first column, so the table must be
 * `expandable` with `showExpandColumn={false}` (CTX-1355).
 */
export function reflectorLogColumns<TRaw>({
  wrapMessages,
  sortable,
  extraColumns,
  hasExpandedRow,
  onFilterPeer,
}: ReflectorLogColumnsOptions<TRaw>): GenericTableColumn<LogRow<TRaw>>[] {
  const chevron: GenericTableColumn<LogRow<TRaw>> = {
    id: "expand",
    header: "",
    width: "56px",
    wrapMode: "nowrap",
    renderCell: (_row, rowIndex, toggleRowExpand, isExpanded) => (
      <ExpandToggle
        isExpanded={Boolean(isExpanded)}
        onToggle={() => toggleRowExpand?.(rowIndex)}
      />
    ),
  };

  return [
    ...(hasExpandedRow ? [chevron] : []),
    {
      id: "timestamp",
      header: "Timestamp",
      width: "136px",
      wrapMode: "truncate",
      sortable,
      sortAccessor: (row) => row.record.timestamp,
      renderCell: (row) => <TimestampCell value={row.record.timestamp} />,
    },
    {
      id: "level",
      header: "Level",
      // Wide enough for the WARNING pill (83 px of content) without cutting it.
      width: "110px",
      wrapMode: "truncate",
      sortable,
      sortAccessor: (row) => row.record.level,
      renderCell: (row) =>
        row.record.level ? (
          <GenericTag
            label={row.record.level.toUpperCase()}
            variant={levelTagVariant(row.record.level)}
            size="small"
            rounded="full"
            minWidth="60px"
          />
        ) : null,
    },
    ...extraColumns,
    {
      id: "message",
      header: "Message",
      wrapMode: wrapMessages ? "break" : "truncate",
      renderCell: (row) => (
        <MessageCell row={row} wrap={wrapMessages} onFilterPeer={onFilterPeer} />
      ),
    },
  ];
}

interface ReflectorLogsTableProps<TRaw> {
  rows: LogRow<TRaw>[];
  wrapMessages: boolean;
  /** Columns the Reflector adds between Level and Message (V2: Reflector, Source). */
  extraColumns?: GenericTableColumn<LogRow<TRaw>>[];
  /** Content of the row the chevron opens; without it the table has no chevron. */
  renderExpanded?: (record: ReflectorLogRecord<TRaw>) => React.ReactNode;
  onRowClick?: (record: ReflectorLogRecord<TRaw>) => void;
  /** Click on the peer's address in a message. */
  onFilterPeer?: (ip: string) => void;
  sortable?: boolean;
  /** Pagination is GenericTable's when left out. */
  initialRowsPerPage?: number;
  rowsPerPageOptions?: number[];
}

const NO_EXTRA_COLUMNS: never[] = [];

/** The logs table V1 (L3 Overview) and V2 (`/reflector-v2/logs`) share. */
export function ReflectorLogsTable<TRaw>({
  rows,
  wrapMessages,
  extraColumns = NO_EXTRA_COLUMNS,
  renderExpanded,
  onRowClick,
  onFilterPeer,
  sortable = false,
  initialRowsPerPage,
  rowsPerPageOptions,
}: ReflectorLogsTableProps<TRaw>) {
  return (
    <GenericTable<LogRow<TRaw>>
      columns={reflectorLogColumns<TRaw>({
        wrapMessages,
        sortable,
        extraColumns,
        hasExpandedRow: Boolean(renderExpanded),
        onFilterPeer,
      })}
      data={rows}
      // Keyed by line, so an open row stays attached to its line when the
      // rows are filtered, grouped or loaded further.
      rowKey={(row) => row.record.key}
      expandable={Boolean(renderExpanded)}
      showExpandColumn={false}
      renderExpanded={renderExpanded ? (row) => renderExpanded(row.record) : undefined}
      onRowClick={onRowClick ? (row) => onRowClick(row.record) : undefined}
      rowClassName={(row) => logRowClassName(row.record.level)}
      initialRowsPerPage={initialRowsPerPage}
      rowsPerPageOptions={rowsPerPageOptions}
      // Grows with its rows: log rows are tall (wrapped messages, grouped lines,
      // expanded details), and a capped body left about one visible row on short
      // screens, with a second scroll inside the page's own.
      overflow="visible"
      // Rows, not lines: a group is one row, and the toolbar counts the lines.
      renderTotal={(count) => `${count} ${count === 1 ? "row" : "rows"}`}
      width="100%"
      // Fixed columns plus Message as the rest of the card: the table fits its
      // card instead of growing to its widest message ("responsive" is min-w-max).
      horizontalScroll="never"
      // 12 px of horizontal padding per cell instead of GenericTable's 16, so the
      // fixed columns fit their content. Column cells only: the expanded row's
      // full-width cell (colspan) keeps its own padding.
      tableClassName="bg-semantic-bg-primary [&>thead>tr>th]:px-3 [&>tbody>tr>td:not([colspan])]:px-3"
    />
  );
}

export default ReflectorLogsTable;
